import {Alert, Badge, Col, Row} from "react-bootstrap"
import IconButton from "../../components/IconButton";
import {faCircleInfo, faEdit, faPlus, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import '../css/ManageCompanyPage.css'
import React, {useEffect, useRef, useState, useContext} from "react";
import {
    deleteGeofence,
    fetchDataFromAllPages,
    getGeofenceExtraKeys,
    getGeofencesForCompany,
    updateGeofenceExtras
} from "../../API/companyApi";
import {toast} from "react-toastify";
import messageFromError from "../../utils/errorUtils";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import RegisterGeofenceModal from "components/Modals/RegisterGeofenceModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ClaimGuard from "../../components/Auth/ClaimGuard";
import {DEL_GEOFENCE, REG_GEOFENCE, UPDATE_COMPANY} from "../../Claims";
import useCompanyId from "../../Hooks/useCompanyId";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import PaginationComponent from "../../components/core/Pagination/PaginationComponent";
import {useTranslation} from "react-i18next";
import Search from "../../components/core/Components/Search";
import ConditionalRender, {Fallback} from "../../components/ConditionalRender";
import StickyPopover from "../../components/StickyPopover";
import {isCurrentTimeBetween} from "../../utils/DateTimeUtills";
import RegisterWorkOrderModal from "../../components/Modals/RegisterWorkOrderModal";
import EditGeofenceModal from "../../components/Modals/EditGeofenceModal";
import MyTable from "../../components/core/Components/MyTable";
import ToggleButtons from "../../components/ToggleButtons";
import GeofencesMap from "../../components/GeofencesMap";
import ExtraColumn from "../../components/extras/ExtraColumn";
import useClaims from "../../Hooks/useClaims";
import MenuItem from "../../components/core/Components/Menu/MenuItem";
import Menu from "../../components/core/Components/Menu/Menu";
import useMyTranslation from "../../Hooks/useMyTranslation";
import {SalvoContext} from "../../contexts/SalvoContext";

export default function GeofencesPage()
{
    const companyId = useCompanyId()
    const {hasClaim} = useClaims()

    const {t} = useTranslation()
    const tOperations = useMyTranslation('global.operations')

    const [modalActive, setModalActive] = useState(false);
    const [geofences, setGeofences] = useState(null)
    const [allGeofences, setAllGeofences] = useState([])

    const [showConfirmDeletion, setShowConfirmDeletion] = useState(false)
    const [showConfirmDeletionSpinner, setShowConfirmDeletionSpinner] = useState(false)
    const geofenceForDeletion = useRef(null)

    const [showAskUserToAddWorkOrdersModal, setShowAskUserToAddWorkOrdersModal] = useState(false)
    const [showAddWorkOrdersModal, setShowAddWorkOrdersModal] = useState(false)

    const [showEditGeofenceModal, setShowEditGeofenceModal] = useState(false)

    const [showConfirmStopAssigningWorkOrders, setShowConfirmStopAssigningWorkOrders] = useState(false)

    const [selectedGeofence, setSelectedGeofence] = useState(null)
    const { geofenceProjectReference } = useContext(SalvoContext);

    const [currentPage, setCurrentPage] = useState(0)
    const [search, setSearch] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const [view, setView] = useState('list')
    const [focusedGeofence, setFocusedGeofence] = useState(null)
    const [pageSize, setPageSize] = useState(10);

    const createdGeofence = useRef(null)

    useEffect(()=> {
        fetchGeofences()
    }, [currentPage, search, pageSize])

    useEffect(() => {
        if(selectedGeofence)
        {
            setSelectedGeofence(geofences.data.find(g => g.id === selectedGeofence.id))
        }
    }, [geofences]);

    async function fetchGeofences(showLoading = true)
    {
        try
        {
            if(showLoading)
                setIsLoading(true)

            const geofencesResponse = await getGeofencesForCompany(companyId, currentPage, pageSize, search, true) // Include enlargement if the user is super admin. E.G has claim GET_ALL_COMPANIES
            setGeofences(geofencesResponse.data)
        }
        catch(err)
        {
            toast.error(messageFromError(err))
        }
        finally {
            if(showLoading)
                setIsLoading(false)
        }
    }

    // The full (unpaginated) geofence list is only ever used by the map view below - fetching it
    // eagerly on every page load meant walking every page of the company's geofence list (~100
    // requests for a company with ~1000 work locations) even when the admin never opens the map.
    // Only fetch it once the map view is actually requested.
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        if(companyId && view === 'map')
        {
            fetchAllGeofences(signal);
        }

        return () => {
            controller.abort(); // Abort ongoing fetch when the component unmounts or dependencies change
        };
    }, [companyId, view]);

    async function fetchAllGeofences(signal) {
        setAllGeofences([]);

        for await (const geofences of fetchDataFromAllPages(`v1/geofence/company/${companyId}`, 0, { includeEnlargement: true }, signal)) {
            geofences.forEach(geofence => {
                setAllGeofences(geofences => [...geofences, geofence]);
            });
        }
    }

    async function onDeleteGeofence()
    {
        try {
            setShowConfirmDeletionSpinner(true)
          
            await deleteGeofence(geofenceForDeletion.current.id)
            await fetchGeofences()
            toast.success(`Work Locations ${geofenceForDeletion.current.name} Was Deleted`)
        } 
        catch (error) {
            toast.error(messageFromError(error))
        }
        finally
        {
            setShowConfirmDeletion(false)
            setShowConfirmDeletionSpinner(false)
        }
        
    }


    // EXTRAS

    const [geofenceExtraKeys, setGeofenceExtraKeys] = useState([])

    useEffect(() => {
        if(!companyId)
            return

        fetchGeofenceExtraKeys()
    }, [companyId]);


    async function fetchGeofenceExtraKeys()
    {
        try {
            const response = await getGeofenceExtraKeys(companyId)
            setGeofenceExtraKeys(response.data)
        }
        catch(ex)
        {
            console.error(ex);
        }
    }


    const GeofenceRow = ({geofence, onFocus}) => {

        const workOrderIsActive = (wo) => {
            return wo && wo.intervals.some(interval => isCurrentTimeBetween(interval.start, interval.end))
        }

        const WorkOrderBadge = ({wo}) => {
            return <div className={'d-flex align-items-center m-1'}>
                <Badge className={'mx-1 w-100 p-2'} style={{cursor: 'default'}} bg={workOrderIsActive(wo) ? 'success' : 'danger'}>{wo.name} ({wo.key})</Badge> {/*onClick={() => navigate('/workorders/' + wo.id)}*/}
            </div>
        }

        const handleRowClick = (e) => {
            setSelectedGeofence(geofence)
            setShowEditGeofenceModal(true)
        }

        return <tr>
            <td onClick={handleRowClick}>{geofence.name}</td>
            <td onClick={handleRowClick}>{geofence.note ?? "-"}</td>
            {/* <td onClick={handleRowClick}>
                <ConditionalRender condition={geofence.workOrders.length > 0}>
                    <Fallback><Badge variant="primary" style={{cursor: 'default'}}>{geofence.workOrders.length}</Badge></Fallback>

                    <StickyPopover delay={100} placement={'right'} component={<div className={'p-2'}>{geofence.workOrders.map(wo => <WorkOrderBadge key={wo.id} wo={wo}/>)}</div>}>
                        <Badge variant="primary" style={{cursor: 'default'}}>{geofence.workOrders.length}</Badge>
                    </StickyPopover>
                </ConditionalRender>
            </td> */}
            <td onClick={handleRowClick}>{geofence.projectNumber || "-"}</td>
            {geofenceProjectReference &&
                geofence.extras?.map((extra) => (
                    <td key={extra.key} onClick={handleRowClick}>
                        {extra.value ?? "-"}
                    </td>
                ))}

            {/* { */}
            <td>
                <div className={'d-flex align-items-center'}>
                    <Menu>
                        <MenuItem text={tOperations('edit')} icon={faEdit} onClick={handleRowClick} />
                        {hasClaim(DEL_GEOFENCE) && <MenuItem text={tOperations('delete')} icon={faTrashCan} variant={'danger'} onClick={() => { geofenceForDeletion.current = geofence; setShowConfirmDeletion(true) }} />}
                    </Menu>
                </div>
            </td>
        </tr>
    }

    const handleGeofenceRegistration = (registeredGeofenceId, registeredGeofenceName) => {
        setModalActive(false);
        // setShowAskUserToAddWorkOrdersModal(true);
        createdGeofence.current = {id: registeredGeofenceId, name: registeredGeofenceName};
        fetchGeofences();
        // Only keep the map's data in sync if the admin is actually looking at it right now -
        // otherwise it'll be fetched fresh next time the map view is opened (see the effect above).
        if(view === 'map')
            fetchAllGeofences();
    }

    const handleGeofenceDeletion = () => {
        fetchGeofences()
        if(view === 'map')
            fetchAllGeofences()
    }

    const handleWorkOrderRegistration = () => {
        setShowAddWorkOrdersModal(false);
        if(selectedGeofence)
        {
            setShowEditGeofenceModal(true)
        }
        fetchGeofences();
    }

    const handleWorkOrderRegistrationCancel = () => {
       setShowAddWorkOrdersModal(false);
       if(!selectedGeofence)
       {
            // setShowConfirmStopAssigningWorkOrders(true)
       }
       else {
           setShowEditGeofenceModal(true)
       }
    }

    const handleGeofenceFocus = (geofence) => {
        setFocusedGeofence(geofence)
        setView('map')
    }

    const handleViewChange = (value) => {
        setView(value)
        if(value === 'list')
        {
            setFocusedGeofence(null)
        }
    }

    const handleExtraKeyEdit = async (keyInfo) => {
        const oldKey = keyInfo.oldKey
        const newKey = keyInfo.newKey

        const updatedKeys = geofenceExtraKeys.map(key => {
            if(key.key === oldKey)
            {
                return {...key, key: newKey}
            }
            return key
        })

        try {
            await updateGeofenceExtras(updatedKeys, companyId)
            setGeofenceExtraKeys(updatedKeys)
        }
        catch(error)
        {
            toast.error(messageFromError(error))
        }
    }

    if (!geofences)
        return <LoadingSpinner/>

    return (
        <div className={`content-full mt-4`}>
            <Row className="justify-content-center">
                <Col>
                    <Row className="mb-2">
                        <Col lg={4} className='d-flex align-items-start flex-column'>
                            <h1 className="mb-0">{t('pages.workLocations.title')}</h1>
                            <Search onSearch={(text) => setSearch(text)}/>
                        </Col>
                        <Col className={'d-flex align-items-end justify-content-center'}>
                            <ToggleButtons
                                value={view}
                                onChange={handleViewChange}
                                buttons={[
                                    {
                                        value: 'list',
                                        text: t('pages.dashboard.list'),
                                    },
                                    {
                                        value: 'map',
                                        text: t('pages.dashboard.map'),
                                    }
                                ]}/>
                        </Col>
                        <Col className={'d-flex align-items-end justify-content-center'}>
                            <div className="d-flex align-items-center gap-2">
                                <label className="me-2">
                                    {/* {t('itemsPerPage')} */}
                                    Items
                                    </label>

                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: 'auto' }}
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(0);     // Reset to page 0
                                        // fetchGeofences();      // Reload
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                </select>  
                            </div>
                        </Col>
                        <Col className="d-flex align-items-end justify-content-end">
                            <ClaimGuard requiredClaim={REG_GEOFENCE}>
                                <IconButton icon={faPlus} color="white" bg="#198754" onClick={() => {
                                    setModalActive(true)
                                }}/>
                            </ClaimGuard>
                            <RegisterGeofenceModal active={modalActive} geofences={geofences.data} onCancel={() => setModalActive(false)} onRegister={handleGeofenceRegistration}/>
                        </Col>
                    </Row>
                        <ConditionalRender condition={!isLoading}>
                            <ConditionalRender condition={geofences.data.length > 0}>
                                {/*Geofences have loaded and there are at least one geofence.*/}
                                    <ConditionalRender condition={view === 'list'}>
                                        <MyTable hover>
                                            <thead>
                                                <tr>
                                                    <th>{t('pages.workLocations.workLocationName')}</th>
                                                    <th>{t('dialogs.note')}</th>
                                                    {/* <th>{t('pages.workLocations.assignedWorkOrders')}</th> */}
                                            <th>{t('pages.workLocations.projectNumber')}</th>
                                            {geofenceProjectReference
                                                ? geofenceExtraKeys.map((key) => (
                                                    <th key={key.key}>
                                                        <ExtraColumn
                                                            extraKey={key.key}
                                                            editable={hasClaim(UPDATE_COMPANY)}
                                                            onEdit={handleExtraKeyEdit}
                                                        />
                                                    </th>
                                                ))
                                                : null}

                                            {/*  */}
                                            <th>{t('global.operations.options')}</th>
                                        </tr>
                                    </thead>
                                            <tbody>
                                                {geofences.data.map(geofence => <GeofenceRow key={geofence.id} geofence={geofence} onFocus={handleGeofenceFocus}/>)}
                                            </tbody>
                                        </MyTable>
                                    </ConditionalRender>

                                    <ConditionalRender condition={view === 'map'}>
                                        <div style={{height: '70vh'}}>
                                            <GeofencesMap geofences={allGeofences} focusedGeofence={focusedGeofence} onDelete={handleGeofenceDeletion}/>
                                        </div>
                                    </ConditionalRender>
                                <Fallback>
                                    <div className='d-flex justify-content-center mt-5'>
                                        <Alert variant='warning' className='w-100'>
                                            <FontAwesomeIcon icon={faCircleInfo}/>
                                            <span className='ms-2'>{t('pages.workLocations.noGeofenceFound')}</span>
                                        </Alert>
                                    </div>
                                </Fallback>
                            </ConditionalRender>

                            <Fallback>
                                <div className={'d-flex justify-content-center align-items-center'}
                                     style={{minHeight: 400}}>
                                    <LoadingSpinner text={t("global.states.loading")} small/>
                                </div>
                            </Fallback>
                        </ConditionalRender>
                    
                    {
                        geofenceForDeletion.current &&
                        <ConfirmModal
                            active={showConfirmDeletion}
                            onCancel={() => setShowConfirmDeletion(false)}
                            onConfirm= {() => {                                 
                                onDeleteGeofence()
                            }}
                            title={t('dialogs.confirmDeleteDialog.title')}
                            showSpinner={showConfirmDeletionSpinner}
                        >
                            {<span>{t('dialogs.confirmDeleteDialog.youAreAboutTo')} <strong style={{color: 'red'}}>{t('dialogs.confirmDeleteDialog.delete')}</strong> <strong>{geofenceForDeletion.current.name}</strong>. {t('dialogs.confirmDeleteDialog.areYouSure')}</span>}
                        </ConfirmModal>
                    }
                </Col>
            </Row>

            <Row className={'mt-4'}>
                <Col className={'d-flex justify-content-center'}>
                    <ConditionalRender condition={view !== 'map'}>
                        <PaginationComponent lastPage={geofences.lastPage} onPageChanged={(page) => setCurrentPage(page)}/>
                    </ConditionalRender>
                </Col>
            </Row>

            <EditGeofenceModal
                active={showEditGeofenceModal}
                geofence={selectedGeofence}
                onConfirm={() => {setSelectedGeofence(null); fetchGeofences(); setShowEditGeofenceModal(false)}}
                onCancel={() => {setSelectedGeofence(null); setShowEditGeofenceModal(false)}}
                onCreateNewWorkOrder={() => {
                    createdGeofence.current = {id: selectedGeofence.id, name: selectedGeofence.name};
                    setShowAddWorkOrdersModal(true);
                    setShowEditGeofenceModal(false)}
                }
                onIntervalsUpdated={fetchGeofences}
            />

            {/* <ConfirmModal active={showAskUserToAddWorkOrdersModal} title={"Assign work order"}
                          positiveButton={{text: t('pages.workOrders.assignWorkOrderDialog.confirm'), variant: "success"}}
                          negativeButtonText={t('pages.workOrders.assignWorkOrderDialog.cancel')}
                          onConfirm={() => {setShowAddWorkOrdersModal(true); setShowAskUserToAddWorkOrdersModal(false)}}
                          onCancel={() => setShowAskUserToAddWorkOrdersModal(false)}>
                <span>{t('pages.workOrders.assignWorkOrderDialog.text')}</span>
            </ConfirmModal> */}

            <RegisterWorkOrderModal active={showAddWorkOrdersModal}
                                    assignToGeofence={createdGeofence.current}
                                    onCancel={handleWorkOrderRegistrationCancel}
                                    onSuccess={handleWorkOrderRegistration}/>

            <ConfirmModal active={showConfirmStopAssigningWorkOrders}
                          title={t('pages.workOrders.stopAssigningWorkOrderDialog.title')}
                          positiveButton={{text: t('pages.workOrders.stopAssigningWorkOrderDialog.confirm'), variant: "danger"}}
                          negativeButtonText={t('pages.workOrders.stopAssigningWorkOrderDialog.cancel')}
                          onConfirm={() => {setShowConfirmStopAssigningWorkOrders(false); fetchGeofences()}}
                          onCancel={() => {setShowConfirmStopAssigningWorkOrders(false); setShowAddWorkOrdersModal(true)}}> {/*Re-open the modal to add work orders.*/}

                <span>{t('pages.workOrders.stopAssigningWorkOrderDialog.text')}</span>
            </ConfirmModal>
        </div>
    )
}